// A ΓÇÿ.tsxΓÇÖ file enables JSX support in the TypeScript compiler, 
// for more information see the following page on the TypeScript wiki:
// https://github.com/Microsoft/TypeScript/wiki/JSX

import { PositionDataModel } from '@syncfusion/ej2-react-popups';
import React from 'react';
import * as Inovx from '@shared';
import FacilityActions from '../actions/FacilityActions';
import FacilityStore from '../stores/FacilityStore';
import KnowledgeBookStore from '../stores/KnowledgeBookStore';
import KnowledgeBookActions from '../actions/KnowledgeBookActions';
import ViewerActions from '../actions/ViewerActions';
import ViewerStore from '../stores/ViewerStore';
import KBDescriptionPopup from './KBDescriptionPopup';
import KnowledgeViews from './knowledgebook/KnowledgeViews';
import ComponentHelpers from '../helpers/ComponentHelpers';
import FacilitySlideoutSessionPanel from './FacilitySlideoutSessionPanel';
import {
    SessionPanelMeasuredContent,
    SessionPanelToolbar,
    type SessionPanelRenderContext,
} from '../../shared/panels/SessionPanel';
import CustomHorizontalSplitter from '../../shared/components/controls/CustomHorizontalSplitter';
import { FACILITY_SLIDEOUT_PANEL_IDS, isFacilitySlideoutPanelFloating } from '../helpers/facilitySlideoutPanelSession';
import ContextMenu, { type ContextMenuItem } from '../viewers/components/ContextMenu';
import UrlUtilities from '@shared/common/UrlUtilities';
import { AssetSetDisplayModeGlyph } from './AssetSetDisplayPriorityEditor';
import AssetSetsActions from '../actions/AssetSetsActions';
import AssetSetVisualizationStore from '../stores/AssetSetVisualizationStore';
import { mergeViewerAssetSets } from '../helpers/viewerAssetSetModel';
import { isPanoCompatibleAssetSet } from '../helpers/assetSetVisualizationModel';

var SystemActions = Inovx.Shared.System.Actions;
const KNOWLEDGE_BOOK_PANEL_ID = FACILITY_SLIDEOUT_PANEL_IDS.knowledgeBook;
const KNOWLEDGE_VIEWS_BOOK_ROW_HEIGHT = 36;
const KNOWLEDGE_VIEWS_SEARCH_ROW_HEIGHT = 36;
const KNOWLEDGE_VIEWS_PANE_GAP = 8;
export const DISPLAY_PRIORITY_SPLITTER_HANDLE_HEIGHT = 10;
export const DISPLAY_PRIORITY_SPLITTER_MIN_PANE_HEIGHT = 120;
export const DISPLAY_PRIORITY_SPLITTER_TOP_RATIO = 0.65;

interface KnowledgeViewDisplayPriorityRow {
    id: string;
    name: string;
    mode: AssetSetDisplayMode;
    color?: number | null;
    invalid?: boolean;
}

const getKnowledgeViewDisplayPriorityRows = (
    knowledgeView: IKnowledgeViewDetail | undefined,
    knowledgeBookAssetSets: readonly IAssetSet[],
    facilityAssetSets: readonly IAssetSet[],
): KnowledgeViewDisplayPriorityRow[] => {
    if (!knowledgeView?.ID) return [];

    const assetSets = mergeViewerAssetSets(knowledgeBookAssetSets, facilityAssetSets);
    const assetSetsByID = new Map(assetSets.map(assetSet => [assetSet.ID.toLocaleLowerCase(), assetSet] as const));
    const entriesByID = new Map((knowledgeView.AssetSets ?? []).map(entry => [entry.ID.toLocaleLowerCase(), entry] as const));
    const specialAssetSets = facilityAssetSets.filter(assetSet =>
        !assetSet.KnowledgeBookID &&
        (assetSet.Type === AssetSetType.All3DAssets || assetSet.Type === AssetSetType.AllAssetTags));
    const specialIDs = new Set(specialAssetSets.map(assetSet => assetSet.ID.toLocaleLowerCase()));
    const rows: KnowledgeViewDisplayPriorityRow[] = [];
    const added = new Set<string>();

    const addRow = (id: string, entry?: IKnowledgeViewAssetSet, defaultMode: AssetSetDisplayMode = 'hidden') => {
        const key = id.toLocaleLowerCase();
        if (added.has(key) || specialIDs.has(key)) return;
        const assetSet = assetSetsByID.get(key);
        if (!assetSet) return;
        rows.push({
            id: assetSet.ID,
            name: assetSet.Type === AssetSetType.All3DAssets
                ? InovxLoader.t('facility:assetSetVisualization.all3DAssets')
                : assetSet.Type === AssetSetType.AllAssetTags
                    ? InovxLoader.t('facility:assetSetVisualization.allAssetTags')
                    : assetSet.Name,
            mode: entry?.Hidden ? 'hidden' : entry?.Color != null ? 'color' : defaultMode,
            color: entry?.Color ?? null,
            invalid: knowledgeView.Type === 1 && !isPanoCompatibleAssetSet(assetSet),
        });
        added.add(key);
    };

    for (const entry of [...(knowledgeView.AssetSets ?? [])].sort((left, right) => (left.Ord ?? 0) - (right.Ord ?? 0))) {
        addRow(entry.ID, entry, 'default');
    }

    const orderedSpecials = specialAssetSets
        .filter(assetSet => knowledgeView.Type !== 1 || assetSet.Type === AssetSetType.AllAssetTags)
        .sort((left, right) => left.Type - right.Type);
    for (const allAssetSet of orderedSpecials) {
        const entry = entriesByID.get(allAssetSet.ID.toLocaleLowerCase());
        rows.push({
            id: allAssetSet.ID,
            name: allAssetSet.Type === AssetSetType.All3DAssets
                ? InovxLoader.t('facility:assetSetVisualization.all3DAssets')
                : InovxLoader.t('facility:assetSetVisualization.allAssetTags'),
            // A missing reference reads as Hidden; a saved one keeps its own state.
            mode: !entry ? 'hidden' : entry.Hidden ? 'hidden' : entry.Color != null ? 'color' : 'default',
            color: entry?.Color ?? null,
        });
    }

    return [...rows.filter(row => !row.invalid), ...rows.filter(row => row.invalid)];
};

const KnowledgeViewDisplayPriority = ({
    knowledgeView,
    knowledgeBookAssetSets,
    facilityAssetSets,
}: {
    knowledgeView?: IKnowledgeViewDetail;
    knowledgeBookAssetSets: readonly IAssetSet[];
    facilityAssetSets: readonly IAssetSet[];
}) => {
    const rows = getKnowledgeViewDisplayPriorityRows(knowledgeView, knowledgeBookAssetSets, facilityAssetSets);

    return (
        <div className="knowledge-views-display-priority">
            <div className="knowledge-views-display-priority-title">
                {InovxLoader.t('facility:assetSetVisualization.displayPriority')}
            </div>
            <div className="knowledge-views-display-priority-header">
                <span>{InovxLoader.t('facility:assetSetVisualization.assetSetColumn')}</span>
                <span>{InovxLoader.t('facility:assetSetVisualization.modeColumn')}</span>
            </div>
            <div className="knowledge-views-display-priority-list">
                {rows.map(row => (
                    <div className={`knowledge-views-display-priority-row${row.invalid ? ' is-invalid' : ''}`} key={row.id}>
                        <span className="display-name-text" title={row.name}>{row.name}</span>
                        <span className="knowledge-views-display-priority-mode"
                            title={row.invalid ? InovxLoader.t('facility:assetSetVisualization.panoInvalidReason') : undefined}>
                            {!row.invalid && <AssetSetDisplayModeGlyph mode={row.mode} color={row.color} />}
                            <span>{row.invalid ? InovxLoader.t('facility:assetSetVisualization.invalid')
                                : InovxLoader.t(`facility:assetSetVisualization.mode.${row.mode}`)}</span>
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

interface KnowledgeBookBarProps {

}

interface KnowledgeBookBarState {
    selectedOntology?: IOntologyPart;
    selectedFacilityID?: string;
    kbBarOpen?: boolean;
    sidebarOpen?: boolean;
    measurementBarOpen?: boolean;
    assetTagbarOpen?: boolean;
    selectedKnowledgeBook?: any;
    knowledgeBookAssetSets?: IAssetSet[];
    facilityAssetSets?: IAssetSet[];
    knowledgeBookViews?: IKnowledgeView[];
    selectedKnowledgeView?: IKnowledgeViewDetail;
    committedKnowledgeView?: IKnowledgeViewDetail;
    dialogPosition?: PositionDataModel;
    is3DViewerVisible?: boolean;
    kbViewingMode?: KnowledgeBookViewingMode;
    selectedAssetSet?: IAssetSet;
    facilityAssetSetsBarOpen?: boolean;
    assemblyLibraryBarOpen?: boolean;
    knowledgeViewEditBarOpen?: boolean;
    knowledgeViewSearchText: string;
    knowledgeBookMenu: { left: number; top: number } | null;
}

function getFormState(): KnowledgeBookBarState {

    return {
        selectedOntology: FacilityStore.getSelectedOntology(),
        selectedFacilityID: FacilityStore.getSelectedFacility()?.ID,
        sidebarOpen: FacilityStore.getSidebarOpen(),
        kbBarOpen: FacilityStore.getKnowledgeBookbarOpen(),
        assetTagbarOpen: FacilityStore.getAssetTagbarOpen(),
        measurementBarOpen: FacilityStore.getMeasurementbarOpen(),
        selectedKnowledgeBook: KnowledgeBookStore.getSelectedKnowledgeBook(),
        knowledgeBookAssetSets: KnowledgeBookStore.getAllAssetSet(),
        knowledgeBookViews: KnowledgeBookStore.getAllKnowledgeView(),
        selectedKnowledgeView: ViewerStore.getSelectedKnowledgeView(),
        committedKnowledgeView: ViewerStore.getCommittedKnowledgeView(),
        is3DViewerVisible: ViewerStore.is3DViewerVisible(),
        kbViewingMode: KnowledgeBookStore.getKnowledgeBookViewingMode(),
        selectedAssetSet: ViewerStore.getSelectedAssetSet(),
        facilityAssetSetsBarOpen: FacilityStore.getFacilityAssetSetsBarOpen(),
        assemblyLibraryBarOpen: FacilityStore.getAssemblyLibrarybarOpen(),
        knowledgeViewEditBarOpen: FacilityStore.getKnowledgeViewEditBarOpen(),
        knowledgeViewSearchText: '',
        knowledgeBookMenu: null,
    };
}
class KnowledgeBookBar extends React.Component<KnowledgeBookBarProps, KnowledgeBookBarState> {
    _isMounted = false;
    private _facilityAssetSetsLoadGeneration = 0;

    kbBarRef: React.RefObject<HTMLDivElement>;
    treeListRef: React.RefObject<HTMLDivElement>;
    knowledgeViewSearchInputRef: React.RefObject<HTMLInputElement>;
    private _restoreKnowledgeViewSearchFocus = false;
    private _knowledgeViewSearchFocusFrame: number | null = null;

    constructor(props: KnowledgeBookBarProps) {
        super(props);
        this.state = {
            ...getFormState(),
            facilityAssetSets: [],
        };
 
        this.kbBarRef = React.createRef();
        this.treeListRef = React.createRef();
        this.knowledgeViewSearchInputRef = React.createRef();
    }

    private _loadState = () => {
        if (this._isMounted) {
            this.setState((prevState) => ({
                ...getFormState(),
                knowledgeViewSearchText: prevState.knowledgeViewSearchText,
                knowledgeBookMenu: prevState.knowledgeBookMenu,
            }));
        }
    }
    componentDidMount() {
        this.changeDialogPosition();
        this._isMounted = true;

        KnowledgeBookStore.addChangeListener(this._onChange);
        FacilityStore.addChangeListener(this._loadState);
        ViewerStore.addChangeListener(this._loadState);
        AssetSetVisualizationStore.addAssetSetsChangeListener(this._handleFacilityAssetSetsChanged);
        void this._loadFacilityAssetSets();
        
        window.addEventListener('beforeunload', this.checkForUnsavedChanges);
    }
    
    componentDidUpdate(prevProps: KnowledgeBookBarProps, prevState: KnowledgeBookBarState) {
        // Always recompute popup position ΓÇö attachment changes don't flip store booleans
        this.changeDialogPosition();

        if (this.state.selectedFacilityID !== prevState.selectedFacilityID) {
            void this._loadFacilityAssetSets();
        }

        if (this.state.selectedKnowledgeBook?.ID !== prevState.selectedKnowledgeBook?.ID) {
            this.setState({ knowledgeViewSearchText: '', knowledgeBookMenu: null });
            if (!this.state.selectedKnowledgeBook?.ID && this.state.kbBarOpen) {
                FacilityActions.closeKBbar();
            }
        }

        const selectedAssetSet = ViewerStore.getSelectedAssetSet();
        if (
            this.state.kbViewingMode !== 'manageAssets' &&
            !this.state.facilityAssetSetsBarOpen &&
            selectedAssetSet?.KnowledgeBookID
        ) {
            requestAnimationFrame(() => {
                ViewerActions.resetSelectedAssetSet();
            })
        }

    }
    componentWillUnmount() {
        this._isMounted = false;
        FacilityStore.removeChangeListener(this._loadState);
        KnowledgeBookStore.removeChangeListener(this._onChange);
        ViewerStore.removeChangeListener(this._loadState);       
        AssetSetVisualizationStore.removeAssetSetsChangeListener(this._handleFacilityAssetSetsChanged);

        window.removeEventListener('beforeunload', this.checkForUnsavedChanges);
        if (this._knowledgeViewSearchFocusFrame != null) {
            cancelAnimationFrame(this._knowledgeViewSearchFocusFrame);
            this._knowledgeViewSearchFocusFrame = null;
        }
    }

    handlePanelOpenChange = (open: boolean) => {
        if (open) {
            FacilityActions.openKBbar();
            return;
        }

        this._closeKBbar();
    }
    
    /**
     * To close KB sidebar
     */
    private _closeKBbar = () => {
        this.setState({ knowledgeBookMenu: null });
        FacilityActions.closeKBbar();
    }

    
    hasUnsavedData = (continuation?: () => void | Promise<unknown>): boolean => {
        return ViewerActions.checkForKnowledgeViewChanges(continuation);
    }

    private _onChange = () => {
        this.setState((prevState) => ({
            ...getFormState(),
            knowledgeViewSearchText: prevState.knowledgeViewSearchText,
            knowledgeBookMenu: prevState.knowledgeBookMenu,
        }));
    }

    private _handleFacilityAssetSetsChanged = (facilityID: string) => {
        if (facilityID === this.state.selectedFacilityID) {
            void this._loadFacilityAssetSets();
        }
    };

    private _loadFacilityAssetSets = async () => {
        const facilityID = FacilityStore.getSelectedFacility()?.ID;
        const generation = ++this._facilityAssetSetsLoadGeneration;
        if (!facilityID) {
            if (this._isMounted) this.setState({ facilityAssetSets: [] });
            return;
        }

        try {
            const assetSets = await AssetSetsActions.refreshFacilityAssetSets(facilityID, false);
            if (!this._isMounted || generation !== this._facilityAssetSetsLoadGeneration) return;
            this.setState({
                facilityAssetSets: (assetSets ?? []).filter(assetSet => !assetSet.KnowledgeBookID),
            });
        } catch (error) {
            console.error('Unable to load Facility Asset Sets for Knowledge View display priority.', error);
        }
    };

    handleCreateKnowledgeView = async () => {
        const kv = await ViewerActions.createNewKnowledgeView(this.state.selectedKnowledgeBook);
        await ViewerActions.openKnowledgeView(kv.ID);
        ViewerActions.setKnowledgeViewDirty(true);
        ViewerActions.beginKnowledgeViewEditSession();
    }
    /**
     * To create KV
     */
    createKnowledgeView = async () => {
        if (!this.state.selectedKnowledgeBook) return;
        if (this.hasUnsavedData(this.createKnowledgeView)) return;
        if (this.state.is3DViewerVisible) {
            const views3D = this.state.knowledgeBookViews?.find((kv) => kv.Type === 2);
            if (!views3D) {
                let message = InovxLoader.t('facility:knowledgeBook.ontologyLimitAdditionalKnowledgeViews')
                    .replace('__ontology__', this.state.selectedOntology?.Name || '');
                SystemActions.displayConfirmationMessage({
                    message: message,
                    confirmCallback: () => {
                        this.handleCreateKnowledgeView();
                    }
                });
            } else {
                this.handleCreateKnowledgeView();
            }
        } else {
            this.handleCreateKnowledgeView();
        }
    }

    resetKnowledgeView() {
        ViewerActions.closeKnowledgeView();
    }

    changeDialogPosition = () => {
        let dialogPosition = { X: 67, Y: 62 };
        const { sidebarOpen, kbBarOpen, assetTagbarOpen, measurementBarOpen, facilityAssetSetsBarOpen, assemblyLibraryBarOpen, knowledgeViewEditBarOpen } = this.state;

        // Only count a panel as open for positioning if it is docked (not floating)
        const kbDockedOpen = kbBarOpen && !isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.knowledgeBook);
        const assetTagDockedOpen = assetTagbarOpen && !isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.assetTags);
        const measurementDockedOpen = measurementBarOpen && !isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.measurements);
        const assetSetsDockedOpen = facilityAssetSetsBarOpen && !isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.facilityAssetSets);
        const assemblyDockedOpen = assemblyLibraryBarOpen && !isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.assemblyLibrary);
        const knowledgeViewEditDockedOpen = knowledgeViewEditBarOpen && !isFacilitySlideoutPanelFloating(FACILITY_SLIDEOUT_PANEL_IDS.knowledgeViewEdit);
        const hasDockedPanel = kbDockedOpen || assetTagDockedOpen || measurementDockedOpen || assetSetsDockedOpen || assemblyDockedOpen || knowledgeViewEditDockedOpen;
        const dockedPanelPosition = knowledgeViewEditDockedOpen
            ? { X: 425, Y: 62 }
            : (kbDockedOpen || assetSetsDockedOpen || assemblyDockedOpen ? { X: 375, Y: 62 } : assetTagDockedOpen ? { X: 395, Y: 62 } : { X: 345, Y: 62 });

        if (sidebarOpen) {
            if (!hasDockedPanel) {
                dialogPosition = { X: 217, Y: 62 };
            } else {
                dialogPosition = dockedPanelPosition;
            }
        } else if (hasDockedPanel) {
            dialogPosition = dockedPanelPosition;
        }

        // Guard against unnecessary re-renders (and infinite loops from always-calling in componentDidUpdate)
        const current = this.state.dialogPosition;
        if (!current || current.X !== dialogPosition.X || current.Y !== dialogPosition.Y) {
            this.setState({ dialogPosition });
        }
    }
    handleOpenDialog = () => {
        let elements = document.getElementById('dialog-heading');
        if (elements) {
            if (elements.scrollWidth > elements.clientWidth) {
                elements.title = elements.textContent.trim();
            }
        }
    }

    handleCloseKBDescriptionPopup = () => {
        if (this.hasUnsavedData(this.handleCloseKBDescriptionPopup)) return;

        FacilityActions.setKnowledgeLibraryCardUrlState({ classificationTagId: undefined });
        KnowledgeBookActions.closeKnowledgeBook();
        this.resetKnowledgeView();
    }

    checkForUnsavedChanges = (event: any) => {
        if (ViewerStore.isEditingKnowledgeView() && ViewerStore.isKnowledgeViewDirty()) {
            event.returnValue = InovxLoader.t("common:msgBox.browserSaveChanges");
        }
    };

    openKnowledgeBookMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        this.setState({
            knowledgeBookMenu: {
                left: rect.left + rect.width - 8,
                top: rect.bottom + 4,
            },
        });
    };

    getKnowledgeBookMenuItems = (): ContextMenuItem[] => {
        const items: ContextMenuItem[] = [{
            id: 'manageAssetSets',
            label: InovxLoader.t('facility:action.manageAssetSets'),
        }];

        if (ComponentHelpers.hasManageKnowledgeBookPermission(this.state.selectedKnowledgeBook)) {
            items.push({
                id: 'deleteKnowledgeBook',
                label: InovxLoader.t('facility:action.deleteKnowledgeBook'),
            });
        }

        return items;
    };

    manageAssetSets = async () => {
        if (!this.state.selectedKnowledgeBook?.ID || this.hasUnsavedData(this.manageAssetSets)) {
            return;
        }

        await ViewerActions.changeKnowledgeBookViewingMode('manageAssets');
        FacilityActions.closeKBbar();
        FacilityActions.openFacilityAssetSetsBar();
    };

    deleteSelectedKnowledgeBook = () => {
        if (this.hasUnsavedData(this.deleteSelectedKnowledgeBook)) return;
        const selectedKnowledgeBook = this.state.selectedKnowledgeBook;
        if (!selectedKnowledgeBook?.ID || !ComponentHelpers.hasManageKnowledgeBookPermission(selectedKnowledgeBook)) {
            return;
        }

        SystemActions.displayConfirmationMessage({
            message: InovxLoader.t('facility:message.confirmDeleteKB'),
            confirmCallback: async () => {
                await KnowledgeBookActions.deleteKnowledgeBook(selectedKnowledgeBook);
                if (ViewerStore.getSelectedAssetSet()?.KnowledgeBookID === selectedKnowledgeBook.ID) {
                    ViewerActions.resetSelectedAssetSet();
                }
                ViewerActions.closeKnowledgeView();
                KnowledgeBookActions.closeKnowledgeBook();
            },
        });
    };

    handleKnowledgeBookMenuItem = (itemId: string) => {
        this.setState({ knowledgeBookMenu: null });
        if (itemId === 'manageAssetSets') {
            void this.manageAssetSets();
        } else if (itemId === 'deleteKnowledgeBook') {
            this.deleteSelectedKnowledgeBook();
        }
    };

    handleKnowledgeViewSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this._restoreKnowledgeViewSearchFocus = document.activeElement === event.currentTarget;
        this.setState({ knowledgeViewSearchText: event.currentTarget.value });
    };

    clearKnowledgeViewSearch = () => {
        this._restoreKnowledgeViewSearchFocus = true;
        this.setState({ knowledgeViewSearchText: '' });
    };

    restoreKnowledgeViewSearchFocus = () => {
        if (!this._restoreKnowledgeViewSearchFocus) {
            return;
        }

        this._restoreKnowledgeViewSearchFocus = false;
        if (this._knowledgeViewSearchFocusFrame != null) {
            cancelAnimationFrame(this._knowledgeViewSearchFocusFrame);
        }

        this._knowledgeViewSearchFocusFrame = requestAnimationFrame(() => {
            this._knowledgeViewSearchFocusFrame = requestAnimationFrame(() => {
                this._knowledgeViewSearchFocusFrame = null;
                const input = this.knowledgeViewSearchInputRef.current;
                if (!input) {
                    return;
                }

                input.focus();
                input.setSelectionRange(input.value.length, input.value.length);
            });
        });
    };

    render() {
        const {
            selectedKnowledgeBook,
            selectedKnowledgeView,
            committedKnowledgeView,
            knowledgeBookAssetSets,
            knowledgeViewSearchText,
            knowledgeBookMenu,
        } = this.state;
        const hasSelectedKnowledgeBook = !!selectedKnowledgeBook?.ID;
        const isKnowledgeBookBarOpen = !!this.state.kbBarOpen && hasSelectedKnowledgeBook;
        let searchBarClass = '';
        if (this.state.sidebarOpen && isKnowledgeBookBarOpen) {
            searchBarClass = 'show-extend';
        } else if (isKnowledgeBookBarOpen) {
            searchBarClass = 'show';
        }

        const canModifySelectedKnowledgeBook = ComponentHelpers.hasModifyKnowledgeBookPermission(selectedKnowledgeBook);

        return (<>
            {this.state.selectedKnowledgeBook && (
                <KBDescriptionPopup
                    kb={this.state.selectedKnowledgeBook}
                    kv={this.state.selectedKnowledgeView}
                    dialogPosition={this.state.dialogPosition}
                    onClose={() => this.handleCloseKBDescriptionPopup()}
                />
            )}
           
            <FacilitySlideoutSessionPanel
                attachedClassName={searchBarClass}
                className="kb-bar-3d"
                elementId="kb-bar-3d-content"
                isOpen={isKnowledgeBookBarOpen}
                keepMounted={true}
                onOpenChange={this.handlePanelOpenChange}
                panelId={KNOWLEDGE_BOOK_PANEL_ID}
                panelRef={this.kbBarRef}
                pinnedWidth={310}
            >
                {(panelContext: SessionPanelRenderContext) => (<>
                    <SessionPanelToolbar
                        context={panelContext}
                        itemClassName="kb-tool-bar-item enabled-btn"
                        listClassName="kb-tool-bar"
                    >
                        <li className="kb-title">{InovxLoader.t('facility:action.knowledgeViews')}</li>
                    </SessionPanelToolbar>

                    <SessionPanelMeasuredContent
                        bottomPadding={10}
                        fallbackOffset={52}
                        minHeight={0}
                    >
                        {measuredHeight => {
                            const availablePaneHeight = Math.max(
                                measuredHeight - DISPLAY_PRIORITY_SPLITTER_HANDLE_HEIGHT,
                                0
                            );
                            const initialKnowledgeViewsHeight = Math.floor(
                                availablePaneHeight * DISPLAY_PRIORITY_SPLITTER_TOP_RATIO
                            );

                            return (
                                <div className="knowledge-views-panel-content" style={{ height: measuredHeight }}>
                                    <CustomHorizontalSplitter
                                        className="knowledge-views-splitter"
                                        containerHeight={measuredHeight}
                                        initialPosition={initialKnowledgeViewsHeight}
                                        minTopHeight={DISPLAY_PRIORITY_SPLITTER_MIN_PANE_HEIGHT}
                                        minBottomHeight={DISPLAY_PRIORITY_SPLITTER_MIN_PANE_HEIGHT}
                                        showBottomPanel
                                        followInitialPositionUntilResized
                                        handleAriaLabel={InovxLoader.t('facility:assetSetVisualization.resizePanels')}
                                        topPanel={topHeight => {
                                            const fixedTopHeight = KNOWLEDGE_VIEWS_SEARCH_ROW_HEIGHT
                                                + KNOWLEDGE_VIEWS_PANE_GAP
                                                + (hasSelectedKnowledgeBook
                                                    ? KNOWLEDGE_VIEWS_BOOK_ROW_HEIGHT + KNOWLEDGE_VIEWS_PANE_GAP
                                                    : 0);
                                            const knowledgeViewsHeight = Math.max(topHeight - fixedTopHeight, 0);

                                            return (
                                                <div className="knowledge-views-list-pane" style={{ height: topHeight }}>
                                                    {hasSelectedKnowledgeBook && (
                                                        <div className="manage-asset-sets-knowledge-book-row">
                                                            <img
                                                                alt=""
                                                                aria-hidden="true"
                                                                className="manage-asset-sets-knowledge-book-icon"
                                                                src={`${UrlUtilities.getBaseApplicationUrl()}/images/KB/KBLarge.png`}
                                                            />
                                                            <span className="display-name-text" title={selectedKnowledgeBook.Name}>
                                                                {selectedKnowledgeBook.Name}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="no-btn manage-asset-sets-context-menu-trigger"
                                                                title={InovxLoader.t('facility:grid.commands')}
                                                                onMouseDown={this.openKnowledgeBookMenu}
                                                            >
                                                                <i className="fa fa-ellipsis-v" />
                                                            </button>
                                                        </div>
                                                    )}

                                                    <div className="knowledge-views-search-row">
                                                        <div className="knowledge-views-search-box">
                                                            <img
                                                                alt=""
                                                                aria-hidden="true"
                                                                src={`${UrlUtilities.getBaseApplicationUrl()}/images/search_light.png`}
                                                            />
                                                            <input
                                                                aria-label={InovxLoader.t('facility:knowledgeBook.searchKnowledgeViewsPanel')}
                                                                onChange={this.handleKnowledgeViewSearchChange}
                                                                placeholder={InovxLoader.t('facility:knowledgeBook.searchKnowledgeViewsPanel')}
                                                                ref={this.knowledgeViewSearchInputRef}
                                                                type="text"
                                                                value={knowledgeViewSearchText}
                                                            />
                                                            {knowledgeViewSearchText && (
                                                                <button
                                                                    type="button"
                                                                    className="no-btn knowledge-views-search-clear"
                                                                    title={InovxLoader.t('facility:assetTagFilter.clear')}
                                                                    onClick={this.clearKnowledgeViewSearch}
                                                                >
                                                                    <img
                                                                        alt=""
                                                                        aria-hidden="true"
                                                                        src={`${UrlUtilities.getBaseApplicationUrl()}/images/tag/close.png`}
                                                                    />
                                                                </button>
                                                            )}
                                                        </div>
                                                        {canModifySelectedKnowledgeBook && (
                                                            <button
                                                                type="button"
                                                                className="knowledge-views-add-button"
                                                                disabled={!hasSelectedKnowledgeBook}
                                                                title={InovxLoader.t('facility:action.capture')}
                                                                aria-label={InovxLoader.t('facility:action.capture')}
                                                                onClick={() => void this.createKnowledgeView()}
                                                            >
                                                                <img
                                                                    alt=""
                                                                    aria-hidden="true"
                                                                    src={`${UrlUtilities.getBaseApplicationUrl()}/images/icons/plus.png`}
                                                                />
                                                            </button>
                                                        )}
                                                    </div>

                                                    <div className="knowledge-views-list" style={{ maxHeight: knowledgeViewsHeight }}>
                                                        {hasSelectedKnowledgeBook && (
                                                            <KnowledgeViews
                                                                key={`kb-views-${selectedKnowledgeBook.ID}`}
                                                                treeListRef={this.treeListRef}
                                                                maxHeight={knowledgeViewsHeight}
                                                                onDataBound={this.restoreKnowledgeViewSearchFocus}
                                                                searchText={knowledgeViewSearchText}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        }}
                                        bottomPanel={bottomHeight => (
                                            <div className="knowledge-views-display-priority-pane" style={{ height: bottomHeight }}>
                                                <KnowledgeViewDisplayPriority
                                                    knowledgeView={committedKnowledgeView ?? selectedKnowledgeView}
                                                    knowledgeBookAssetSets={knowledgeBookAssetSets ?? []}
                                                    facilityAssetSets={this.state.facilityAssetSets ?? []}
                                                />

                                            </div>
                                        )}
                                    />
                                </div>
                            );
                        }}
                    </SessionPanelMeasuredContent>
                </>)}
            </FacilitySlideoutSessionPanel>
            {knowledgeBookMenu && hasSelectedKnowledgeBook && (
                <ContextMenu
                    left={knowledgeBookMenu.left}
                    top={knowledgeBookMenu.top}
                    items={this.getKnowledgeBookMenuItems()}
                    onItemClick={this.handleKnowledgeBookMenuItem}
                    onRequestClose={() => this.setState({ knowledgeBookMenu: null })}
                    containerId="knowledge-book-context-menu"
                    zIndex={110001}
                />
            )}
            </>
        )
    }
}

export default KnowledgeBookBar;
